import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import Logo from '../common/Logo';
import SafeIcon from '../../common/SafeIcon';

const ADMIN_MENU = [
  {
    title: 'Main',
    items: [
      { name: 'Dashboard', icon: 'Home', path: '/dashboard' },
    ]
  },
  {
    title: 'Recruitment',
    items: [
      { name: 'Analytics', icon: 'PieChart', path: '/analytics' },
      { name: 'Positions', icon: 'Briefcase', path: '/positions' },
      { name: 'Applications', icon: 'FileText', path: '/applications' },
      { name: 'Review Workflow', icon: 'GitPullRequest', path: '/workflow' },
      { name: 'Candidates', icon: 'Users', path: '/candidates' },
      { name: 'Score Insights', icon: 'BarChart2', path: '/score-insights' },
    ]
  },
  {
    title: 'Evaluations',
    items: [
      { name: 'Profile Reviews', icon: 'Clipboard', path: '/profile-reviews' },
      { name: 'Interview Feedback', icon: 'MessageSquare', path: '/interview-feedback' },
    ]
  },
  {
    title: 'System',
    items: [
      { name: 'Communication', icon: 'Mail', path: '/communication' },
      { name: 'Notifications', icon: 'Bell', path: '/notifications' },
      { name: 'Staff', icon: 'UserCheck', path: '/staff' },
      { name: 'Settings', icon: 'Settings', path: '/settings' },
    ]
  }
];

const CANDIDATE_MENU = [
  {
    title: 'Discovery',
    items: [
      { name: 'Browse Jobs', icon: 'Briefcase', path: '/jobs' },
      { name: 'Applications', icon: 'FileText', path: '/my-applications' },
    ]
  },
  {
    title: 'Profile',
    items: [
      { name: 'Resume Builder', icon: 'FilePlus', path: '/resume' },
      { name: 'My Profile', icon: 'User', path: '/profile' },
      { name: 'Notifications', icon: 'Bell', path: '/notifications' },
    ]
  }
];

const Sidebar = ({ isOpen, setIsOpen, isCollapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Use localStorage for robust role detection
  const userRole = localStorage.getItem('userRole');
  const isCandidate = userRole === 'candidate' || (!userRole && (location.pathname.startsWith('/jobs') || location.pathname === '/profile' && !location.pathname.includes('admin')));

  const menuGroups = isCandidate ? CANDIDATE_MENU : ADMIN_MENU;
  const [expandedGroups, setExpandedGroups] = useState(['Main', 'Recruitment', 'Discovery', 'Profile']);

  const toggleGroup = (title) => {
    setExpandedGroups(prev => 
      prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]
    );
  };

  const handleLogout = () => {
    localStorage.removeItem('userRole');
    navigate('/');
  };

  return (
    <>
      {/* Mobile Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar Content */}
      <aside 
        className={`fixed top-0 left-0 z-30 h-screen bg-brand-dark text-[#CFD3EC] transition-all duration-300 flex flex-col shadow-xl 
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'} 
          ${isCollapsed ? 'w-20' : 'w-64'}`}
      >
        {/* Logo Area */}
        <div className={`h-16 flex items-center border-b border-white/10 shrink-0 ${isCollapsed ? 'justify-center px-0' : 'px-6'}`}>
          <Logo className={isCollapsed ? "h-8 w-8" : "h-10 w-auto"} collapsed={isCollapsed} />
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto py-6 px-3 space-y-4 scrollbar-hide">
          {menuGroups.map((group) => (
            <div key={group.title} className="space-y-1">
              {!isCollapsed && (
                <button 
                  onClick={() => toggleGroup(group.title)}
                  className="w-full flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-2 px-3 hover:text-white transition-colors"
                >
                  {group.title}
                  <SafeIcon name={expandedGroups.includes(group.title) ? "ChevronDown" : "ChevronRight"} size={12} />
                </button>
              )}
              
              {(!isCollapsed && expandedGroups.includes(group.title) || isCollapsed) && group.items.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.path}
                  className={({ isActive }) => 
                    `flex items-center rounded-lg transition-all duration-200 group
                    ${isCollapsed ? 'justify-center py-3' : 'px-3 py-2.5 gap-3'}
                    ${isActive 
                      ? 'bg-gradient-to-r from-brand-primary to-brand-secondary text-white shadow-md' 
                      : 'hover:bg-white/5 hover:text-white'}`
                  }
                >
                  <SafeIcon name={item.icon} size={20} />
                  {!isCollapsed && <span className="font-medium whitespace-nowrap">{item.name}</span>}
                </NavLink>
              ))}
            </div>
          ))}
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t border-white/10 shrink-0">
          <button 
            onClick={handleLogout}
            className={`flex items-center text-[#00AEEF] hover:text-white hover:bg-brand-secondary/20 transition-all rounded-lg w-full
              ${isCollapsed ? 'justify-center py-3' : 'px-3 py-2.5 gap-3'}`}
          >
            <SafeIcon name="LogOut" size={20} />
            {!isCollapsed && <span className="font-medium">Logout</span>}
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;